import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { finalize } from 'rxjs';

import { Return } from '../../models/return.model';
import { ReturnStatus } from '../../models/return-status.enum';
import { ReturnsService } from '../../services/returns.service';

type StatusFilter = 'ALLE' | ReturnStatus;
type SortColumn = 'filiale' | 'status' | 'wert' | 'quote';
type SortDirection = 'asc' | 'desc';

interface SortState {
  column: SortColumn | null;
  direction: SortDirection;
}

const PRIORITY_QUOTE_SCHWELLE = 5;
const PRIORITY_WERT_SCHWELLE = 1500;

@Component({
  selector: 'app-returns-uebersicht',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './returns-uebersicht.component.html',
  styleUrl: './returns-uebersicht.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReturnsUebersichtComponent {
  private readonly returnsService = inject(ReturnsService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly ReturnStatus = ReturnStatus;

  protected readonly selectedFiliale = signal('ALLE');
  protected readonly selectedFilter = signal<StatusFilter>(ReturnStatus.Offen);
  protected readonly sortState = signal<SortState>({
    column: null,
    direction: 'asc',
  });
  protected readonly isFilialeDropdownOpen = signal(false);
  protected readonly returns = signal<Return[]>([]);

  protected readonly isLoading = signal(true);
  protected readonly skeletonSummaryCards = Array.from({ length: 3 });
  protected readonly skeletonFilterTabs = Array.from({ length: 4 });
  protected readonly skeletonRows = Array.from({ length: 6 });

  protected readonly filialen = computed(() => {
    const filialenMap = new Map<string, string>();

    for (const retoure of this.returns()) {
      filialenMap.set(String(retoure.filialNummer), retoure.filialeName);
    }

    return [
      { value: 'ALLE', label: 'Alle Filialen' },
      ...Array.from(filialenMap.entries()).map(([value, name]) => ({
        value,
        label: name ? `Filiale ${value} - ${name}` : `Filiale ${value}`,
      })),
    ];
  });

  protected readonly selectedFilialeLabel = computed(() => {
    return (
      this.filialen().find((filiale) => filiale.value === this.selectedFiliale())
        ?.label ?? 'Alle Filialen'
    );
  });

  protected readonly filialeFilteredReturns = computed(() => {
    const selectedFiliale = this.selectedFiliale();

    if (selectedFiliale === 'ALLE') {
      return this.returns();
    }

    return this.returns().filter(
      (retoure) => String(retoure.filialNummer) === selectedFiliale
    );
  });

  protected readonly filteredReturns = computed(() => {
    const filteredReturns = this.filialeFilteredReturns().filter((retoure) =>
      this.matchesFilter(retoure, this.selectedFilter())
    );

    return this.sortReturns(filteredReturns);
  });

  // Summary Cards: immer über alle Filialen
  protected readonly summaryOffenCount = computed(
    () =>
      this.returns().filter(
        (entry) => entry.status !== ReturnStatus.Erfasst
      ).length
  );

  protected readonly summaryHeuteWert = computed(() =>
    this.returns()
      .filter((entry) => this.istHeute(entry.retourenTag))
      .reduce((sum, entry) => sum + entry.wert, 0)
  );

  protected readonly summaryHeutePositionen = computed(() =>
    this.returns()
      .filter((entry) => this.istHeute(entry.retourenTag))
      .reduce((sum, entry) => sum + entry.anzahlPositionen, 0)
  );

  // Filter Tabs, Tabelle und Footer: abhängig von der ausgewählten Filiale
  protected readonly offenCount = computed(
    () =>
      this.filialeFilteredReturns().filter(
        (entry) => entry.status !== ReturnStatus.Erfasst
      ).length
  );

  protected readonly inBearbeitungCount = computed(
    () =>
      this.filialeFilteredReturns().filter(
        (entry) => entry.status === ReturnStatus.InBearbeitung
      ).length
  );

  protected readonly erfasstCount = computed(
    () =>
      this.filialeFilteredReturns().filter(
        (entry) =>
          entry.status === ReturnStatus.Erfasst
      ).length
  );

  protected readonly totalCount = computed(
    () => this.filialeFilteredReturns().length
  );

  protected readonly erledigtCount = computed(() => this.erfasstCount());

  protected readonly footerWertHeute = computed(() => this.summaryHeuteWert());

  protected readonly statusFilters = computed(() => [
    {
      label: 'Offen',
      value: ReturnStatus.Offen,
      count: this.offenCount(),
    },
    {
      label: 'In Bearbeitung',
      value: ReturnStatus.InBearbeitung,
      count: this.inBearbeitungCount(),
    },
    {
      label: 'Erfasst',
      value: ReturnStatus.Erfasst,
      count: this.erfasstCount(),
    },
    {
      label: 'Alle',
      value: 'ALLE' as const,
      count: this.totalCount(),
    },
  ]);

  constructor() {
    this.isLoading.set(true);

    this.returnsService
      .getReturns()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (returns) => {
          this.returns.set(returns);
        },
        error: () => {
          this.returns.set([]);
        },
      });
  }

  @HostListener('document:click', ['$event'])
  protected closeFilialeDropdownOnOutsideClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isFilialeDropdownOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected closeFilialeDropdownOnEscape(): void {
    this.isFilialeDropdownOpen.set(false);
  }

  protected toggleFilialeDropdown(): void {
    this.isFilialeDropdownOpen.update((state) => !state);
  }

  protected selectFiliale(filialeValue: string): void {
    this.selectedFiliale.set(filialeValue);
    this.isFilialeDropdownOpen.set(false);
  }

  protected setFilter(filter: StatusFilter): void {
    this.selectedFilter.set(filter);
  }

  protected setSort(column: SortColumn): void {
    const currentSort = this.sortState();

    const firstDirection: SortDirection =
      column === 'wert' || column === 'quote' ? 'desc' : 'asc';

    const secondDirection: SortDirection =
      firstDirection === 'asc' ? 'desc' : 'asc';

    if (currentSort.column !== column) {
      this.sortState.set({ column, direction: firstDirection });

      return;
    }

    if (currentSort.direction === firstDirection) {
      this.sortState.set({ column, direction: secondDirection });

      return;
    }

    this.sortState.set({ column: null, direction: 'asc' });
  }

  protected isSortedBy(column: SortColumn): boolean {
    return this.sortState().column === column;
  }

  protected getSortIcon(column: SortColumn): string {
    if (!this.isSortedBy(column)) {
      return 'unfold_more';
    }

    return this.sortState().direction === 'asc'
      ? 'keyboard_arrow_up'
      : 'keyboard_arrow_down';
  }

  protected isSummaryActive(filter: StatusFilter): boolean {
    return this.selectedFilter() === filter;
  }

  protected openReturn(retoure: Return): void {
    this.router.navigate(['/retouren', retoure.filialNummer]);
  }

  protected isUeberfaelligSichtbar(retoure: Return): boolean {
    return (
      retoure.istUeberfaellig && retoure.status !== ReturnStatus.Erfasst
    );
  }

  protected getStatusLabel(status: ReturnStatus): string {
    switch (status) {
      case ReturnStatus.Offen:
        return 'Offen';
      case ReturnStatus.InBearbeitung:
        return 'In Bearbeitung';
      case ReturnStatus.Erfasst:
        return 'Erfasst';
    }
  }

  protected getStatusClass(status: ReturnStatus): string {
    switch (status) {
      case ReturnStatus.Offen:
        return 'status-offen';
      case ReturnStatus.InBearbeitung:
        return 'status-in-bearbeitung';
      case ReturnStatus.Erfasst:
        return 'status-abgeschlossen';
    }
  }

  // Same wording as the Bestellungen overview.
  protected getActionLabel(retoure: Return): string {
    if (retoure.status === ReturnStatus.Erfasst) {
      return 'Ansehen';
    }

    if (retoure.status === ReturnStatus.InBearbeitung) {
      return 'Öffnen';
    }

    return 'Bearbeiten';
  }

  protected formatWert(wert: number): string {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(wert);
  }

  protected formatQuote(quote: number): string {
    return `${quote.toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}%`;
  }

  private matchesFilter(retoure: Return, filter: StatusFilter): boolean {
    if (filter === 'ALLE') {
      return true;
    }

    if (filter === ReturnStatus.Offen) {
      return retoure.status !== ReturnStatus.Erfasst;
    }

    if (filter === ReturnStatus.Erfasst) {
      return (
        retoure.status === ReturnStatus.Erfasst
      );
    }

    return retoure.status === filter;
  }

  private sortReturns(returns: Return[]): Return[] {
    const currentSort = this.sortState();

    if (currentSort.column === null) {
      return [...returns].sort(
        (first, second) => this.calcPriority(first) - this.calcPriority(second)
      );
    }

    return [...returns].sort((first, second) => {
      if (currentSort.column === 'filiale') {
        const filialeCompare = first.filialeName.localeCompare(
          second.filialeName,
          'de',
          { sensitivity: 'base', numeric: true }
        );

        if (filialeCompare !== 0) {
          return currentSort.direction === 'asc'
            ? filialeCompare
            : filialeCompare * -1;
        }

        return first.filialNummer - second.filialNummer;
      }

      if (currentSort.column === 'status') {
        const result =
          this.getStatusSortValue(first.status) -
          this.getStatusSortValue(second.status);

        return currentSort.direction === 'asc' ? result : result * -1;
      }

      if (currentSort.column === 'wert') {
        const result = first.wert - second.wert;

        return currentSort.direction === 'asc' ? result : result * -1;
      }

      const result = first.quote - second.quote;

      return currentSort.direction === 'asc' ? result : result * -1;
    });
  }

  private istHeute(retourenTag: string): boolean {
    if (!retourenTag) {
      return false;
    }

    const retourenDatum = new Date(retourenTag);

    if (Number.isNaN(retourenDatum.getTime())) {
      return false;
    }

    const heute = new Date();

    return (
      retourenDatum.getFullYear() === heute.getFullYear() &&
      retourenDatum.getMonth() === heute.getMonth() &&
      retourenDatum.getDate() === heute.getDate()
    );
  }

  private calcPriority(retoure: Return): number {
    if (retoure.status === ReturnStatus.Erfasst) {
      return 9999;
    }

    let score = 0;

    if (retoure.istUeberfaellig) {
      score -= 800;
    }

    if (retoure.quote > PRIORITY_QUOTE_SCHWELLE) {
      score -= 600;
    }

    if (retoure.wert > PRIORITY_WERT_SCHWELLE) {
      score -= 500;
    }

    if (retoure.status === ReturnStatus.Offen) {
      score -= 300;
    }

    score -= retoure.wert / 10;

    return score;
  }

  private getStatusSortValue(status: ReturnStatus): number {
    switch (status) {
      case ReturnStatus.Offen:
        return 0;
      case ReturnStatus.InBearbeitung:
        return 1;
      case ReturnStatus.Erfasst:
        return 2;
    }
  }
}